import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import DashboardNav from './components/DashboardNav';
import OverviewTab from './components/OverviewTab';
import PropertyTab from './components/PropertyTab';
import FinancialsTab from './components/FinancialsTab';
import MaintenanceTab from './components/MaintenanceTab';
import DocumentsTab from './components/DocumentsTab';
import useSEO from '@/hooks/useSEO';

interface Client {
  name: string;
  property: string;
  code: string;
}

export default function PortalDashboard() {
  const navigate = useNavigate();
  const [client, setClient] = useState<Client | null>(null);
  const [activeTab, setActiveTab] = useState('overview');

  useSEO({
    title: 'Dashboard | Higher Standard Global Owner Portal',
    description: 'Secure owner dashboard for Higher Standard Global clients.',
    canonical: '/portal/dashboard',
    noIndex: true,
  });

  useEffect(() => {
    const stored = sessionStorage.getItem('hsg_client');
    if (!stored) {
      navigate('/portal');
      return;
    }
    try {
      setClient(JSON.parse(stored));
    } catch {
      navigate('/portal');
    }
  }, [navigate]);

  if (!client) {
    return (
      <div className="min-h-screen bg-[#FAFAF8] flex items-center justify-center">
        <div className="flex items-center gap-3 text-[#6B6B6B]">
          <i className="ri-loader-4-line animate-spin text-xl"></i>
          <span className="text-sm">Loading your portal...</span>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F7F5F2] flex flex-col">
      <DashboardNav client={client} activeTab={activeTab} onTabChange={setActiveTab} />
      <main className="flex-1 w-full px-6 md:px-10 py-8 max-w-7xl mx-auto">
        {activeTab === 'overview' && <OverviewTab client={client} onTabChange={setActiveTab} />}
        {activeTab === 'property' && <PropertyTab client={client} />}
        {activeTab === 'financials' && <FinancialsTab />}
        {activeTab === 'maintenance' && <MaintenanceTab />}
        {activeTab === 'documents' && <DocumentsTab />}
      </main>
      <footer className="w-full px-6 md:px-10 py-5 border-t border-[#E8E6E3] bg-white">
        <div className="max-w-7xl mx-auto flex flex-col sm:flex-row items-center justify-between gap-2">
          <p className="text-xs text-[#AEAAA4]">&copy; {new Date().getFullYear()} Higher Standard Global. All rights reserved.</p>
          <p className="text-xs text-[#AEAAA4] italic">Secure Client Portal · By Reference Only</p>
        </div>
      </footer>
    </div>
  );
}
